import React from 'react';
import { ArrowRight, BookOpen, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getGeoKnowledgeArticle,
  getGeoKnowledgeArticlePath,
} from '../data/geoKnowledgeIndex';
import { getGeoNewsArticleById, getGeoNewsArticlePath } from '../data/geoNewsIndex';
import OptimizedImage from './OptimizedImage';
import { prefetchGeoKnowledgeArticle, prefetchGeoNewsArticle } from '../utils/articlePrefetch';

const HOME_ARTICLE_IDS = [
  '456',
  '455',
  '451',
  '448',
  '447',
  '446',
  '445',
  '444',
  '454',
  '453',
  '441',
  '439',
];

const GeoKnowledgeHomeSection: React.FC = () => {
  const articles = HOME_ARTICLE_IDS.map((id) => {
    const libraryArticle = getGeoKnowledgeArticle(id);
    if (libraryArticle) {
      return {
        id: `library-${id}`,
        kind: 'knowledge' as const,
        articleId: libraryArticle.id,
        title: libraryArticle.title,
        summary: libraryArticle.summary,
        date: libraryArticle.date,
        cover: libraryArticle.cover,
        route: getGeoKnowledgeArticlePath(libraryArticle),
      };
    }
    const newsArticle = getGeoNewsArticleById(id);
    return newsArticle
      ? {
          id: `news-${newsArticle.category}-${id}`,
          kind: 'news' as const,
          articleId: newsArticle.id,
          category: newsArticle.category,
          title: newsArticle.title,
          summary: newsArticle.summary,
          date: newsArticle.date,
          cover: newsArticle.cover,
          route: getGeoNewsArticlePath(newsArticle),
        }
      : undefined;
  }).filter((article): article is NonNullable<typeof article> => Boolean(article));

  const prefetchArticle = (
    article:
      | (typeof articles)[number]
      | undefined,
  ) => {
    if (!article) return;
    if (article.kind === 'knowledge') {
      prefetchGeoKnowledgeArticle(article.articleId);
      return;
    }
    prefetchGeoNewsArticle(article.category, article.articleId);
  };

  return (
    <section data-knowledge-section className="bg-[#F5F6F8] py-16 md:py-20">
      <div className="mx-auto max-w-[1400px] px-6">
        <header className="mb-12 text-center">
          <h2 className="text-3xl font-black tracking-tight text-[#111827] md:text-4xl">
            GEO知识库
          </h2>
          <p className="mx-auto mt-3 max-w-3xl text-base leading-7 text-slate-500">
            提供GEO优化相关知识、行业资讯和技术文章，帮助企业了解最新的GEO优化趋势
          </p>
        </header>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={article.route}
              onMouseEnter={() => prefetchArticle(article)}
              onFocus={() => prefetchArticle(article)}
              onTouchStart={() => prefetchArticle(article)}
              onMouseDown={() => prefetchArticle(article)}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)] transition-all hover:-translate-y-1 hover:border-[#D83A40]/35 hover:shadow-[0_18px_45px_rgba(88,12,18,0.10)]"
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-[#0B0D13]">
                <OptimizedImage
                  src={article.cover}
                  alt={article.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/35 px-3 py-1 text-[11px] font-bold text-white backdrop-blur">
                  GEO优化方法
                </span>
              </div>

              <div className="flex flex-1 flex-col p-6">
                <h3 className="line-clamp-2 text-lg font-black leading-7 text-slate-900 transition-colors group-hover:text-[#B7262D]">
                  {article.title}
                </h3>
                <p className="mt-3 line-clamp-3 text-sm leading-7 text-slate-500">
                  {article.summary}
                </p>
                <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-5 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-slate-400">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {article.date.slice(0, 10)}
                  </span>
                  <span className="inline-flex items-center gap-1 font-bold text-[#B7262D]">
                    阅读更多 <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/knowledge"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#D83A40] to-[#B7262D] px-8 py-3.5 text-sm font-black text-white shadow-[0_10px_26px_rgba(183,38,45,0.22)] transition-transform hover:-translate-y-0.5"
          >
            <BookOpen className="h-4 w-4" />
            查看更多GEO优化知识
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default GeoKnowledgeHomeSection;
